import React, { useEffect, useState } from 'react';
import { Link, NavLink, Navigate, Outlet, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Users, MousePointerClick, ClipboardList,
  LogOut, ShieldCheck, Menu, X, Search, Layers,
} from 'lucide-react';
import { useAdminAuth } from './AuthContext';

const NAV = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/admin/discover', label: 'Discover', icon: Search },
  { to: '/admin/pipeline', label: 'Enrich & analyze', icon: Layers },
  { to: '/admin/prospects', label: 'Prospects', icon: ClipboardList },
  { to: '/admin/clicks', label: 'Showcase clicks', icon: MousePointerClick },
  { to: '/admin/users', label: 'Users', icon: Users, adminOnly: true },
];

// Discourage indexing of any /admin URL. Kept here in addition to robots.txt
// because you can't fully trust crawlers that ignore robots.
function useNoIndex() {
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow, noarchive';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);
}

export default function AdminLayout() {
  const { user, status, logout } = useAdminAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  useNoIndex();

  // Close the drawer on every route change.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center text-text-muted">
        Loading…
      </div>
    );
  }
  if (status === 'anon') {
    return <Navigate to="/admin/login" replace state={{ from: location.pathname + location.search }} />;
  }

  const visible = NAV.filter((n) => !n.adminOnly || user.role === 'admin');

  return (
    <div className="min-h-screen md:flex">
      {/* Mobile top bar */}
      <header className="md:hidden sticky top-0 z-30 flex items-center justify-between px-4 py-3 bg-surface/95 border-b border-border backdrop-blur">
        <Link to="/admin" className="flex items-center gap-2">
          <span className="w-7 h-7 rounded-md bg-gradient-brand flex items-center justify-center font-display font-bold text-white text-sm">S</span>
          <span className="font-display font-bold">Sudco<span className="gradient-text">.</span></span>
          <span className="ml-1 text-[10px] uppercase tracking-wider text-text-muted px-1.5 py-0.5 rounded bg-surface-2 border border-border">admin</span>
        </Link>
        <button
          onClick={() => setMobileOpen(true)}
          aria-label="Open menu"
          className="p-2 rounded-lg border border-border bg-surface-2 text-text"
        >
          <Menu size={18} />
        </button>
      </header>

      {/* Backdrop (mobile only, when drawer is open) */}
      {mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          className="md:hidden fixed inset-0 z-40 bg-black/60"
          aria-hidden="true"
        />
      )}

      {/* Sidebar — drawer on mobile, static on desktop */}
      <aside
        className={`
          fixed md:static inset-y-0 left-0 z-50 w-60 shrink-0
          border-r border-border bg-surface md:bg-surface/50
          flex flex-col transition-transform duration-200
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0
        `}
      >
        <div className="flex items-center justify-between px-5 py-5 border-b border-border">
          <Link to="/admin" className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-gradient-brand flex items-center justify-center font-display font-bold text-white">
              S
            </span>
            <span className="font-display font-bold text-text">Sudco<span className="gradient-text">.</span></span>
            <span className="ml-1 text-[10px] uppercase tracking-wider text-text-muted px-1.5 py-0.5 rounded bg-surface-2 border border-border">
              admin
            </span>
          </Link>
          <button
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
            className="md:hidden p-1.5 rounded-lg text-text-muted hover:text-text hover:bg-surface-2"
          >
            <X size={18} />
          </button>
        </div>

        <nav className="flex-1 py-3 overflow-y-auto">
          {visible.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-5 py-2.5 text-sm transition-colors ${
                    isActive
                      ? 'text-text bg-surface-2 border-l-2 border-accent'
                      : 'text-text-muted hover:text-text hover:bg-surface-2/40 border-l-2 border-transparent'
                  }`
                }
              >
                <Icon size={16} />
                {item.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="border-t border-border px-5 py-4 text-xs text-text-muted">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck size={14} className={user.role === 'admin' ? 'text-accent' : ''} />
            <span className="text-text truncate">{user.email}</span>
          </div>
          <div className="mb-3 capitalize">{user.role}</div>
          <button
            onClick={logout}
            className="w-full flex items-center justify-center gap-2 bg-surface-2 hover:bg-surface border border-border rounded-lg px-3 py-1.5 text-text"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 px-4 py-5 md:px-8 md:py-8">
        <Outlet />
      </main>
    </div>
  );
}
